import {
  CadImportedDataFlags,
  CadRecordDataPoint,
  CadRecordDataPoints,
} from '../features/cadImport/store/cadImport.types'
import {
  IDispositionOption,
  IHospitalOption,
  ITripInformationOptionsState,
} from '../features/options/store/options.types'
import { IPcr } from '../features/pcr/store/pcr.types'
import { transformDateTimeToMonthDayYear } from './dateTime.util'
import Logger from '../services/logger.service'
import { ICommonAddressesOption } from '../features/pickupAddress/store/pickupAddress.types'
import { hasAllProperties } from './object.util'
import { hideDisableDateTimesBasedOnDisposition } from '../features/tripInformation/utils/tripInformation.util'
import { ITimesState } from '../features/times/store/times.types'

export const transformCADData = (
  importedCadData: CadRecordDataPoints,
  dataPoint: CadRecordDataPoint,
  index: number,
  cadImportedDataFlags: CadImportedDataFlags
) => {
  if (dataPoint.id === 'PT_DOB') {
    return (importedCadData[index].value = transformDateTimeToMonthDayYear(dataPoint.value))
  } else if (dataPoint.id === 'PT_AGE') {
    cadImportedDataFlags.hasAge = true
    return (importedCadData[index].value = dataPoint.value.toString())
  } else if (dataPoint.id === 'MEDICAL_CATEGORY') {
    cadImportedDataFlags.hasPatientCategory = true
  }
}

export const updateNemsis35Disposition = (
  pcr: IPcr,
  tripInformationOptions: ITripInformationOptionsState,
  times: ITimesState,
  importedCadData: CadRecordDataPoint[]
) => {
  try {
    if (pcr.patient?.tripInformation?.disposition) {
      const dispositionOptionId = pcr.patient?.tripInformation?.disposition?.id
      const {
        dispositionOptions,
        unitDispositionOptions,
        patientEvaluationOptions,
        crewDispositionOptions,
        transportDispositionOptions,
        refusalReleaseReasonOptions,
      } = tripInformationOptions

      const dispositionOption: IDispositionOption | undefined = dispositionOptions.find(
        (option) => option.id === dispositionOptionId
      )
      if (dispositionOption) {
        const updatedTimesWithDispositionConfig = hideDisableDateTimesBasedOnDisposition(
          dispositionOption,
          times,
          importedCadData
        )
        const defaultUnitDisposition = unitDispositionOptions?.find(
          (option) => option.id === dispositionOption.nemsis3_unit_dispo
        )
        const defaultPatientEvaluation = patientEvaluationOptions?.find(
          (option) => option.id === dispositionOption.nemsis3_pt_eval
        )
        const defaultCrewDisposition = crewDispositionOptions?.find(
          (option) => option.id === dispositionOption.nemsis3_crew_dispo
        )
        const defaultTransportDisposition = transportDispositionOptions?.find(
          (option) => option.id === dispositionOption.nemsis3_transport_dispo
        )
        const defaultRefusalReleaseReason = refusalReleaseReasonOptions?.find(
          (option) => option.id === dispositionOption.nemsis3_release_reason
        )
        pcr.patient.tripInformation.unitDisposition = defaultUnitDisposition || null
        pcr.patient.tripInformation.patientEvaluation = defaultPatientEvaluation || null
        pcr.patient.tripInformation.crewDisposition = defaultCrewDisposition || null
        pcr.patient.tripInformation.transportDisposition = defaultTransportDisposition || null
        pcr.times = updatedTimesWithDispositionConfig
        if (defaultRefusalReleaseReason)
          pcr.patient.tripInformation.refusalReleaseReason = [defaultRefusalReleaseReason]
        else pcr.patient.tripInformation.refusalReleaseReason = []
      }
    }
  } catch (e) {
    Logger.error(`Unable to updateNemsis35Disposition - Reason: ${e}`)
  }
}

export const clearPickupAddressLocationData = (pcr: IPcr) => {
  pcr.patient.pickupAddress.commonAddress = null
  pcr.patient.pickupAddress.location = ''
  pcr.patient.pickupAddress.addressLine1 = ''
  pcr.patient.pickupAddress.addressLine2 = ''
  pcr.patient.pickupAddress.aptSuiteRoom = ''
  pcr.patient.pickupAddress.milePostMajorRoadway = ''
  pcr.patient.pickupAddress.crossStreetDirections = ''
  pcr.patient.pickupAddress.city = null
  pcr.patient.pickupAddress.county = null
  pcr.patient.pickupAddress.country = null
  pcr.patient.pickupAddress.state = null
  pcr.patient.pickupAddress.zip = ''
  pcr.patient.pickupAddress.censusTract = ''
  pcr.patient.pickupAddress.mCD = ''
  pcr.patient.pickupAddress.stateIds = null
  pcr.patient.pickupAddress.referringGPSE = ''
  pcr.patient.pickupAddress.referringGPSN = ''
  pcr.patient.pickupAddress.referringUSNG = ''
}

export const clearDestinationAddressLocationData = (pcr: IPcr) => {
  pcr.disposition.destination.commonAddress = null
  pcr.disposition.destination.name = ''
  pcr.disposition.destination.addressLine1 = ''
  pcr.disposition.destination.addressLine2 = ''
  pcr.disposition.destination.aptSuiteRoom = ''
  pcr.disposition.destination.milePostMajorRoadway = ''
  pcr.disposition.destination.crossStreetDirections = ''
  pcr.disposition.destination.city = null
  pcr.disposition.destination.county = null
  pcr.disposition.destination.country = null
  pcr.disposition.destination.state = null
  pcr.disposition.destination.zip = ''
  pcr.disposition.destination.censusTract = ''
  pcr.disposition.destination.stateIds = null
  pcr.disposition.destination.gpse = ''
  pcr.disposition.destination.gpsn = ''
  pcr.disposition.destination.usng = ''
}

export const hasRefLocationChanged = (pcr: IPcr, importedCadData: CadRecordDataPoints) => {
  if (pcr.patient?.pickupAddress) {
    const { addressLine1, addressLine2, state, city, country, county, zip } = pcr.patient.pickupAddress
    if (city) {
      const refCity = importedCadData.find((cadData) => cadData.id === 'REF_CITY')
      if (refCity && refCity.value?.name?.toLowerCase() !== city.name?.toLowerCase()) {
        return true
      }
    }
    if (state) {
      const refState = importedCadData.find((cadData) => cadData.id === 'REF_STATE')
      if (refState && refState.value?.id !== state.id) return true
    }
    if (county) {
      const refCounty = importedCadData.find((cadData) => cadData.id === 'REF_COUNTY')
      if (refCounty && refCounty.value?.id !== county.id) return true
    }
    if (addressLine1) {
      const refAddr = importedCadData.find((cadData) => cadData.id === 'REF_ADDR')
      if (refAddr && refAddr.value?.toLowerCase() !== addressLine1?.toLowerCase()) return true
    }
    if (addressLine2) {
      const refAddr2 = importedCadData.find((cadData) => cadData.id === 'REF_ADDR2')
      if (refAddr2 && refAddr2.value?.toLowerCase() !== addressLine2?.toLowerCase()) return true
    }
    if (country) {
      const refCountry = importedCadData.find((cadData) => cadData.id === 'REF_COUNTRY')
      if (refCountry && refCountry.value?.id !== country.id) return true
    }
    if (zip) {
      const refZip = importedCadData.find((cadData) => cadData.id === 'REF_ZIP')
      if (refZip && refZip.value !== zip) return true
    }
  }
  return false
}

export const hasRecLocationChanged = (pcr: IPcr, importedCadData: CadRecordDataPoints) => {
  if (pcr?.disposition?.destination) {
    const { addressLine1, addressLine2, state, city, country, county, zip } = pcr.disposition.destination
    if (city) {
      const recCity = importedCadData.find((cadData) => cadData.id === 'REC_CITY')
      if (recCity && recCity.value?.name?.toLowerCase() !== city.name?.toLowerCase()) return true
    }
    if (state) {
      const recState = importedCadData.find((cadData) => cadData.id === 'REC_STATE')
      if (recState && recState.value?.id !== state.id) return true
    }
    if (county) {
      const recCounty = importedCadData.find((cadData) => cadData.id === 'REC_COUNTY')
      if (recCounty && recCounty.value?.id !== county.id) return true
    }
    if (addressLine1) {
      const recAddr = importedCadData.find((cadData) => cadData.id === 'REC_ADDR')
      if (recAddr && recAddr.value?.toLowerCase() !== addressLine1?.toLowerCase()) return true
    }
    if (addressLine2) {
      const recAddr2 = importedCadData.find((cadData) => cadData.id === 'REC_ADDR2')
      if (recAddr2 && recAddr2.value?.toLowerCase() !== addressLine2?.toLowerCase()) return true
    }
    if (country) {
      const recCountry = importedCadData.find((cadData) => cadData.id === 'REC_COUNTRY')
      if (recCountry && recCountry.value?.id !== country.id) return true
    }
    if (zip) {
      const recZip = importedCadData.find((cadData) => cadData.id === 'REC_ZIP')
      if (recZip && recZip.value !== zip) return true
    }
  }
  return false
}

export const isValidLocationObj = (obj: IHospitalOption | ICommonAddressesOption): boolean => {
  const locationKeys = ['address1', 'address2', 'city', 'state', 'zip', 'county', 'country']
  return hasAllProperties(obj, locationKeys)
}
